import type { InputState } from '../types';
import type { MusicBands } from '../../audio/MusicEngine';
import type {
  City,
  DronePowerUpType,
  EngineStats,
  FloatingText,
  GamePhase,
  InterceptorRocket,
  OrbitalBomber,
  Particle,
  Shockwave,
  Silo,
  SupplyDrone,
  Vector2D,
  Warhead,
  WarheadType,
} from './types';

export class SonicSiloEngine {
  public readonly width = 800;
  public readonly height = 600;

  public phase: GamePhase = 'START';
  public score = 0;
  public wave = 1;
  public combo = 0;
  public maxCombo = 0;

  public reticle: Vector2D = { x: 400, y: 260 };
  public reticleSpeed = 450;
  public gravityPulseActive = false;
  public tripleShotTimer = 0;

  public silos: Silo[] = [];
  public cities: City[] = [];
  public interceptors: InterceptorRocket[] = [];
  public shockwaves: Shockwave[] = [];
  public warheads: Warhead[] = [];
  public bombers: OrbitalBomber[] = [];
  public supplyDrones: SupplyDrone[] = [];
  public particles: Particle[] = [];
  public floatingTexts: FloatingText[] = [];

  // Audio-visual event counters for Game shell SFX/shake hooks
  public shotsFiredCount = 0;
  public explosionsTriggeredCount = 0;
  public powerUpsTriggeredCount = 0;
  public groundImpactsCount = 0;

  private warheadsSpawned = 0;
  private totalWarheadsToSpawn = 10;
  private spawnTimer = 0;
  private bomberSpawnTimer = 6.0;
  private droneSpawnTimer = 8.0;
  private waveClearTimer = 0;
  private nextId = 1;

  constructor() {
    this.reset();
  }

  public reset(): void {
    this.phase = 'START';
    this.score = 0;
    this.wave = 1;
    this.combo = 0;
    this.maxCombo = 0;
    this.reticle = { x: this.width / 2, y: 260 };
    this.gravityPulseActive = false;
    this.tripleShotTimer = 0;

    this.interceptors = [];
    this.shockwaves = [];
    this.warheads = [];
    this.bombers = [];
    this.supplyDrones = [];
    this.particles = [];
    this.floatingTexts = [];

    this.warheadsSpawned = 0;
    this.totalWarheadsToSpawn = 10;
    this.spawnTimer = 0;
    this.bomberSpawnTimer = 6.0;
    this.droneSpawnTimer = 8.0;
    this.waveClearTimer = 0;

    this.initSilos();
    this.initCities();
  }

  private initSilos(): void {
    const positions = [100, 400, 700];
    this.silos = positions.map((x, idx) => ({
      id: idx,
      x,
      y: 550,
      ammo: 10,
      maxAmmo: 10,
      active: true,
      reloadTimer: 0,
      reloadInterval: 2.4,
    }));
  }

  private initCities(): void {
    const positions = [210, 300, 500, 590];
    this.cities = positions.map((x, idx) => ({
      id: idx,
      x,
      y: 555,
      hp: 100,
      maxHp: 100,
      active: true,
    }));
  }

  public startGame(): void {
    this.reset();
    this.startWave(1);
  }

  public setReticlePosition(x: number, y: number): void {
    this.reticle.x = Math.max(20, Math.min(this.width - 20, x));
    this.reticle.y = Math.max(30, Math.min(this.height - 90, y));
  }

  public update(dt: number, input: InputState, musicBands?: MusicBands): void {
    const clampedDt = Math.min(dt, 0.1);

    this.updateParticles(clampedDt);
    this.updateFloatingTexts(clampedDt);

    if (this.phase === 'START') {
      if (input.firePressed) {
        this.startGame();
      }
      return;
    }

    if (this.phase === 'GAME_OVER') {
      if (input.firePressed) {
        this.startGame();
      }
      return;
    }

    if (this.phase === 'WAVE_CLEAR') {
      this.waveClearTimer -= clampedDt;
      this.updateShockwaves(clampedDt, musicBands);
      this.updateInterceptors(clampedDt);
      if (this.waveClearTimer <= 0) {
        this.startWave(this.wave + 1);
      }
      return;
    }

    // PLAYING phase
    this.handleInput(clampedDt, input, musicBands);
    this.updateSilos(clampedDt);
    this.updateSpawning(clampedDt, musicBands);
    this.updateInterceptors(clampedDt);
    this.updateWarheads(clampedDt);
    this.updateBombers(clampedDt);
    this.updateSupplyDrones(clampedDt);
    this.updateShockwaves(clampedDt, musicBands);
    this.checkCollisions();
    this.checkWaveState();
  }

  private handleInput(dt: number, input: InputState, musicBands?: MusicBands): void {
    let dx = 0;
    let dy = 0;
    if (input.left) dx -= 1;
    if (input.right) dx += 1;
    if (input.up) dy -= 1;
    if (input.down) dy += 1;

    if (dx !== 0 && dy !== 0) {
      const inv = 1 / Math.sqrt(2);
      dx *= inv;
      dy *= inv;
    }

    this.setReticlePosition(
      this.reticle.x + dx * this.reticleSpeed * dt,
      this.reticle.y + dy * this.reticleSpeed * dt
    );

    if (this.tripleShotTimer > 0) {
      this.tripleShotTimer = Math.max(0, this.tripleShotTimer - dt);
    }

    // Holding fire activates the Gravity Pulse Deflection Aura around the reticle!
    this.gravityPulseActive = Boolean(input.fire);

    if (input.firePressed) {
      const isBeatPeak = Boolean(
        musicBands && (musicBands.beat > 0.55 || musicBands.kick > 0.55)
      );
      this.fireRocket(this.reticle.x, this.reticle.y, isBeatPeak);
    }
  }

  public fireRocket(targetX?: number, targetY?: number, empCharged = false): boolean {
    if (this.phase !== 'PLAYING') return false;

    const tx = targetX ?? this.reticle.x;
    const ty = Math.min(targetY ?? this.reticle.y, this.height - 90);

    // Find closest active silo with ammo > 0
    let bestSilo: Silo | null = null;
    let minDist = Infinity;

    for (const silo of this.silos) {
      if (silo.active && silo.ammo > 0) {
        const d = Math.hypot(silo.x - tx, silo.y - ty);
        if (d < minDist) {
          minDist = d;
          bestSilo = silo;
        }
      }
    }

    if (!bestSilo) return false;

    bestSilo.ammo -= 1;
    this.shotsFiredCount += 1;

    const spawnInterceptor = (destX: number, destY: number) => {
      const rocket: InterceptorRocket = {
        id: `rocket_${this.nextId++}`,
        startX: bestSilo!.x,
        startY: bestSilo!.y - 12,
        x: bestSilo!.x,
        y: bestSilo!.y - 12,
        targetX: Math.max(20, Math.min(this.width - 20, destX)),
        targetY: Math.max(30, Math.min(this.height - 90, destY)),
        speed: 620,
        siloId: bestSilo!.id,
        empCharged,
      };
      this.interceptors.push(rocket);
    };

    spawnInterceptor(tx, ty);

    // Triple Salvo power-up launches two flanking interceptors for free!
    if (this.tripleShotTimer > 0) {
      spawnInterceptor(tx - 45, ty + 10);
      spawnInterceptor(tx + 45, ty + 10);
    }

    return true;
  }

  private updateSilos(dt: number): void {
    for (const silo of this.silos) {
      if (!silo.active) continue;
      if (silo.ammo < silo.maxAmmo) {
        silo.reloadTimer += dt;
        if (silo.reloadTimer >= silo.reloadInterval) {
          silo.reloadTimer = 0;
          silo.ammo += 1;
        }
      } else {
        silo.reloadTimer = 0;
      }
    }
  }

  private updateSpawning(dt: number, musicBands?: MusicBands): void {
    // 1. Spawn Ballistic Warheads
    if (this.warheadsSpawned < this.totalWarheadsToSpawn) {
      this.spawnTimer -= dt;
      const energyBoost = musicBands ? musicBands.energy * 0.4 : 0;
      const baseInterval = Math.max(0.4, 1.8 - this.wave * 0.12 - energyBoost);

      if (this.spawnTimer <= 0) {
        this.spawnTimer = baseInterval;
        this.spawnWarhead();
        this.warheadsSpawned += 1;
      }
    }

    // 2. Spawn Orbital Bombers on Wave 2+
    if (this.wave >= 2 && this.warheadsSpawned < this.totalWarheadsToSpawn) {
      this.bomberSpawnTimer -= dt;
      if (this.bomberSpawnTimer <= 0) {
        this.bomberSpawnTimer = Math.max(7.0, 14.0 - this.wave);
        this.spawnOrbitalBomber();
      }
    }

    // 3. Spawn Supply Drones periodically
    this.droneSpawnTimer -= dt;
    if (this.droneSpawnTimer <= 0) {
      this.droneSpawnTimer = 11.0;
      this.spawnSupplyDrone();
    }
  }

  public spawnOrbitalBomber(y = 95): OrbitalBomber {
    const fromLeft = this.nextId % 2 === 0;
    const bomber: OrbitalBomber = {
      id: `bomber_${this.nextId++}`,
      x: fromLeft ? -20 : this.width + 20,
      y,
      vx: (fromLeft ? 1 : -1) * (75 + this.wave * 6),
      radius: 14,
      hp: 2,
      dropTimer: 1.6,
    };
    this.bombers.push(bomber);
    return bomber;
  }

  public spawnSupplyDrone(forcedType?: DronePowerUpType, y = 130): SupplyDrone {
    const types: DronePowerUpType[] = ['repair', 'emp', 'triple'];
    const type = forcedType ?? types[this.nextId % types.length];
    const fromLeft = this.nextId % 2 === 1;
    const drone: SupplyDrone = {
      id: `drone_${this.nextId++}`,
      type,
      x: fromLeft ? -15 : this.width + 15,
      y,
      vx: (fromLeft ? 1 : -1) * 110,
      radius: 12,
    };
    this.supplyDrones.push(drone);
    return drone;
  }

  private updateInterceptors(dt: number): void {
    for (let i = this.interceptors.length - 1; i >= 0; i--) {
      const r = this.interceptors[i];
      const dx = r.targetX - r.x;
      const dy = r.targetY - r.y;
      const dist = Math.hypot(dx, dy);
      const step = r.speed * dt;

      if (dist <= step) {
        this.triggerShockwave(
          r.targetX,
          r.targetY,
          false,
          1,
          r.empCharged ? '#00ffaa' : '#00ffff',
          r.empCharged
        );
        this.addParticles(r.targetX, r.targetY, r.empCharged ? '#00ffaa' : '#00ffff', 12);
        this.interceptors.splice(i, 1);
      } else {
        r.x += (dx / dist) * step;
        r.y += (dy / dist) * step;
      }
    }
  }

  public spawnWarhead(
    forcedType?: WarheadType,
    customStart?: Vector2D,
    customTarget?: Vector2D
  ): Warhead {
    const startX = customStart ? customStart.x : 40 + Math.random() * (this.width - 80);
    const startY = customStart ? customStart.y : 0;

    // Pick a target among active silos and active cities
    const targets: Vector2D[] = [
      ...this.silos.filter((s) => s.active).map((s) => ({ x: s.x, y: s.y })),
      ...this.cities.filter((c) => c.active).map((c) => ({ x: c.x, y: c.y })),
    ];
    const chosenTarget =
      customTarget ??
      (targets.length > 0
        ? targets[Math.floor(Math.random() * targets.length)]
        : { x: this.width / 2, y: 550 });

    const targetX = chosenTarget.x + (customTarget ? 0 : (Math.random() - 0.5) * 24);
    const targetY = chosenTarget.y;

    let type: WarheadType = forcedType ?? 'standard';
    if (!forcedType) {
      const roll = Math.random();
      if (this.wave >= 2 && roll < 0.22) {
        type = 'mirv';
      } else if (this.wave >= 2 && roll < 0.42) {
        type = 'fast';
      } else if (this.wave >= 3 && roll < 0.58) {
        type = 'heavy';
      }
    }

    let speed = 70 + this.wave * 8;
    let radius = 6;
    let hp = 1;
    let scoreValue = 100;
    let color = '#ff0055';

    if (type === 'fast') {
      speed *= 1.8;
      radius = 4;
      scoreValue = 150;
      color = '#ffff00';
    } else if (type === 'heavy') {
      speed *= 0.6;
      radius = 12;
      hp = 3;
      scoreValue = 300;
      color = '#ff8800';
    } else if (type === 'mirv') {
      scoreValue = 200;
      color = '#ff00ff';
    } else if (type === 'bomber_bomb') {
      speed *= 1.15;
      radius = 5;
      scoreValue = 120;
      color = '#ff4488';
    }

    const warhead: Warhead = {
      id: `warhead_${this.nextId++}`,
      type,
      startX,
      startY,
      targetX,
      targetY,
      x: startX,
      y: startY,
      speed,
      radius,
      hp,
      maxHp: hp,
      scoreValue,
      color,
      splitCount: type === 'mirv' ? 2 : 0,
      hasSplit: false,
      stasisTimer: 0,
    };

    this.warheads.push(warhead);
    return warhead;
  }

  private updateWarheads(dt: number): void {
    const gravityRadius = 150;

    for (let i = this.warheads.length - 1; i >= 0; i--) {
      const w = this.warheads[i];

      if (w.stasisTimer > 0) {
        w.stasisTimer = Math.max(0, w.stasisTimer - dt);
        continue;
      }

      // Gravity Pulse Aura gently pulls nearby warheads toward the reticle to cluster them!
      if (this.gravityPulseActive) {
        const gdx = this.reticle.x - w.x;
        const gdy = this.reticle.y - w.y;
        const gDist = Math.hypot(gdx, gdy);
        if (gDist > 8 && gDist <= gravityRadius) {
          const pullStrength = (1 - gDist / gravityRadius) * 190;
          w.x += (gdx / gDist) * pullStrength * dt;
          w.y += (gdy / gDist) * pullStrength * dt * 0.5;
        }
      }

      const dx = w.targetX - w.startX;
      const dy = w.targetY - w.startY;
      const dist = Math.hypot(dx, dy);

      if (dist > 0) {
        w.x += (dx / dist) * w.speed * dt;
        w.y += (dy / dist) * w.speed * dt;
      }

      // MIRV splitting logic at 35% height
      if (w.type === 'mirv' && !w.hasSplit && w.y >= this.height * 0.35) {
        w.hasSplit = true;
        for (let s = 0; s < 2; s++) {
          const offsetX = (s === 0 ? -1 : 1) * (40 + Math.random() * 40);
          this.spawnWarhead(
            'standard',
            { x: w.x, y: w.y },
            { x: w.targetX + offsetX, y: w.targetY }
          );
        }
      }

      // Check ground collision
      if (w.y >= w.targetY - 5 || w.y >= this.height - 40) {
        this.onWarheadImpactGround(w);
        this.warheads.splice(i, 1);
      }
    }
  }

  private updateBombers(dt: number): void {
    for (let i = this.bombers.length - 1; i >= 0; i--) {
      const b = this.bombers[i];
      b.x += b.vx * dt;
      b.dropTimer -= dt;

      if (b.dropTimer <= 0 && b.x > 60 && b.x < this.width - 60) {
        b.dropTimer = 1.8;
        this.spawnWarhead('bomber_bomb', { x: b.x, y: b.y + 10 });
      }

      if ((b.vx > 0 && b.x > this.width + 40) || (b.vx < 0 && b.x < -40)) {
        this.bombers.splice(i, 1);
      }
    }
  }

  private updateSupplyDrones(dt: number): void {
    for (let i = this.supplyDrones.length - 1; i >= 0; i--) {
      const d = this.supplyDrones[i];
      d.x += d.vx * dt;
      if ((d.vx > 0 && d.x > this.width + 30) || (d.vx < 0 && d.x < -30)) {
        this.supplyDrones.splice(i, 1);
      }
    }
  }

  private onWarheadImpactGround(w: Warhead): void {
    this.groundImpactsCount += 1;
    this.triggerShockwave(w.x, w.y, true, 1, '#ff3300', false);
    this.addParticles(w.x, w.y, w.color, 15);

    // 1. Check if impact hits an active City Shield Dome within 42px
    let hitCity: City | null = null;
    let minCityDist = Infinity;
    for (const city of this.cities) {
      if (city.active) {
        const d = Math.hypot(city.x - w.x, city.y - w.y);
        if (d < minCityDist) {
          minCityDist = d;
          hitCity = city;
        }
      }
    }

    if (hitCity && minCityDist <= 42) {
      const dmg = w.type === 'heavy' ? 60 : 50;
      hitCity.hp = Math.max(0, hitCity.hp - dmg);
      if (hitCity.hp <= 0) {
        hitCity.active = false;
        this.addFloatingText('CITY DESTROYED!', hitCity.x, hitCity.y - 24, '#ff0044');
      } else {
        this.addFloatingText(`SHIELD -${dmg}%`, hitCity.x, hitCity.y - 20, '#ff8800');
      }
    } else {
      // 2. Otherwise check if impact hits an active Silo within 50px
      let hitSilo: Silo | null = null;
      let minSiloDist = Infinity;
      for (const silo of this.silos) {
        if (silo.active) {
          const d = Math.hypot(silo.x - w.x, silo.y - w.y);
          if (d < minSiloDist) {
            minSiloDist = d;
            hitSilo = silo;
          }
        }
      }

      if (hitSilo && minSiloDist <= 50) {
        hitSilo.active = false;
        hitSilo.ammo = 0;
        this.addFloatingText('SILO DESTROYED!', hitSilo.x, hitSilo.y - 20, '#ff0044');
      }
    }

    // Game Over if all silos are destroyed OR all cities are destroyed
    const allSilosDead = this.silos.every((s) => !s.active);
    const allCitiesDead = this.cities.every((c) => !c.active);
    if (allSilosDead || allCitiesDead) {
      this.phase = 'GAME_OVER';
    }
  }

  public triggerShockwave(
    x: number,
    y: number,
    isSecondary: boolean,
    comboChain: number,
    color = '#00ffff',
    empCharged = false
  ): Shockwave {
    const id = `sw_${this.nextId++}`;
    const baseRadius = empCharged ? 65 : 55;
    const maxRadius = isSecondary ? 45 + Math.min(35, comboChain * 6) : baseRadius;
    const shockwave: Shockwave = {
      id,
      x,
      y,
      radius: 4,
      maxRadius,
      expansionRate: isSecondary ? 145 : 165,
      age: 0,
      maxAge: empCharged ? 0.65 : 0.5,
      isSecondary,
      comboChain,
      color,
      empCharged,
      hitEntities: new Set<string>(),
    };
    this.shockwaves.push(shockwave);
    this.explosionsTriggeredCount += 1;
    return shockwave;
  }

  private updateShockwaves(dt: number, musicBands?: MusicBands): void {
    const rateMultiplier = musicBands ? 1 + musicBands.kick * 0.4 + musicBands.energy * 0.2 : 1;

    for (let i = this.shockwaves.length - 1; i >= 0; i--) {
      const sw = this.shockwaves[i];
      sw.age += dt;
      sw.radius += sw.expansionRate * rateMultiplier * dt;

      if (sw.radius >= sw.maxRadius || sw.age >= sw.maxAge) {
        this.shockwaves.splice(i, 1);
      }
    }
  }

  private checkCollisions(): void {
    for (const sw of this.shockwaves) {
      // 1. Check Warheads against Shockwave (using hitEntities so multi-HP warheads take 1 hit per shockwave!)
      for (let i = this.warheads.length - 1; i >= 0; i--) {
        const w = this.warheads[i];
        if (sw.hitEntities.has(w.id)) continue;

        const distSq = (w.x - sw.x) ** 2 + (w.y - sw.y) ** 2;
        const hitDist = sw.radius + w.radius;

        if (distSq <= hitDist * hitDist) {
          sw.hitEntities.add(w.id);
          w.hp -= 1;
          this.addParticles(w.x, w.y, w.color, 6);

          if (sw.empCharged && w.hp > 0) {
            w.stasisTimer = Math.max(w.stasisTimer, 1.8);
            this.addFloatingText('EMP STASIS!', w.x, w.y - 12, '#00ffaa');
          }

          if (w.hp <= 0) {
            this.warheads.splice(i, 1);

            const comboMultiplier = Math.min(256, Math.pow(2, sw.comboChain - 1));
            const points = w.scoreValue * comboMultiplier;
            this.score += points;
            this.combo = Math.max(this.combo, sw.comboChain);
            this.maxCombo = Math.max(this.maxCombo, this.combo);

            const text = sw.comboChain > 1 ? `+${points} (${sw.comboChain}x)` : `+${points}`;
            const cascadeHueColor =
              sw.comboChain > 1 ? `hsl(${(sw.comboChain * 55) % 360}, 100%, 60%)` : '#00ffff';
            this.addFloatingText(text, w.x, w.y, cascadeHueColor);

            this.triggerShockwave(
              w.x,
              w.y,
              true,
              sw.comboChain + 1,
              cascadeHueColor,
              sw.empCharged
            );
            this.addParticles(w.x, w.y, '#ffffff', 18);
          }
        }
      }

      // 2. Check Orbital Bombers against Shockwave
      for (let i = this.bombers.length - 1; i >= 0; i--) {
        const b = this.bombers[i];
        if (sw.hitEntities.has(b.id)) continue;

        const distSq = (b.x - sw.x) ** 2 + (b.y - sw.y) ** 2;
        if (distSq <= (sw.radius + b.radius) ** 2) {
          sw.hitEntities.add(b.id);
          b.hp -= 1;
          this.addParticles(b.x, b.y, '#ff00aa', 10);
          if (b.hp <= 0) {
            this.bombers.splice(i, 1);
            const pts = 400 * sw.comboChain;
            this.score += pts;
            this.addFloatingText(`BOMBER DOWN +${pts}!`, b.x, b.y, '#ff00aa');
            this.triggerShockwave(b.x, b.y, true, sw.comboChain + 1, '#ff00aa', sw.empCharged);
          }
        }
      }

      // 3. Check Supply Drones against Shockwave
      for (let i = this.supplyDrones.length - 1; i >= 0; i--) {
        const d = this.supplyDrones[i];
        const distSq = (d.x - sw.x) ** 2 + (d.y - sw.y) ** 2;
        if (distSq <= (sw.radius + d.radius) ** 2) {
          this.supplyDrones.splice(i, 1);
          this.activateDronePowerUp(d);
        }
      }
    }
  }

  public activateDronePowerUp(drone: SupplyDrone): void {
    this.powerUpsTriggeredCount += 1;
    this.addParticles(drone.x, drone.y, '#00ff88', 20);

    // Refill all active silos on any supply drop
    for (const silo of this.silos) {
      if (silo.active) silo.ammo = silo.maxAmmo;
    }

    if (drone.type === 'repair') {
      // Repair a destroyed or damaged city or silo
      const deadCity = this.cities.find((c) => !c.active || c.hp < c.maxHp);
      const deadSilo = this.silos.find((s) => !s.active);
      if (deadCity) {
        deadCity.active = true;
        deadCity.hp = deadCity.maxHp;
        this.addFloatingText('CITY SHIELD RESTORED!', deadCity.x, deadCity.y - 25, '#00ff66');
      } else if (deadSilo) {
        deadSilo.active = true;
        deadSilo.ammo = deadSilo.maxAmmo;
        this.addFloatingText('SILO REBUILT!', deadSilo.x, deadSilo.y - 25, '#00ff66');
      } else {
        this.score += 500;
        this.addFloatingText('SUPPLY BONUS +500!', drone.x, drone.y, '#00ff66');
      }
    } else if (drone.type === 'emp') {
      // Screen-clearing Harmonic EMP Burst!
      const count = this.warheads.length;
      for (const w of this.warheads) {
        this.addParticles(w.x, w.y, '#00ffff', 12);
      }
      this.warheads = [];
      const bonus = Math.max(300, count * 150);
      this.score += bonus;
      this.addFloatingText(`EMP OVERLOAD +${bonus}!`, drone.x, drone.y, '#00ffff');
    } else if (drone.type === 'triple') {
      this.tripleShotTimer = 8.0;
      this.addFloatingText('TRIPLE SALVO ONLINE!', drone.x, drone.y, '#ffcc00');
    }
  }

  private checkWaveState(): void {
    if (this.phase !== 'PLAYING') return;

    if (
      this.warheadsSpawned >= this.totalWarheadsToSpawn &&
      this.warheads.length === 0 &&
      this.bombers.length === 0
    ) {
      this.phase = 'WAVE_CLEAR';
      this.waveClearTimer = 2.0;

      for (const silo of this.silos) {
        if (silo.active) {
          silo.ammo = silo.maxAmmo;
        }
      }

      // Heal surviving cities by +25 HP on wave clear
      for (const city of this.cities) {
        if (city.active) {
          city.hp = Math.min(city.maxHp, city.hp + 25);
        }
      }

      this.addFloatingText(`WAVE ${this.wave} CLEARED!`, this.width / 2, this.height / 2, '#00ffcc');
    }
  }

  private startWave(waveNum: number): void {
    this.wave = waveNum;
    this.phase = 'PLAYING';
    this.warheadsSpawned = 0;
    this.totalWarheadsToSpawn = 8 + waveNum * 4;
    this.spawnTimer = 1.0;
    this.bomberSpawnTimer = 5.5;
    this.combo = 0;

    // Restore 1 lost silo every 3rd wave if available
    if (waveNum % 3 === 0) {
      const deadSilo = this.silos.find((s) => !s.active);
      if (deadSilo) {
        deadSilo.active = true;
        deadSilo.ammo = deadSilo.maxAmmo;
        this.addFloatingText('SILO REPAIRED!', deadSilo.x, deadSilo.y - 20, '#00ff66');
      }
    }
  }

  private addParticles(x: number, y: number, color: string, count: number): void {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 30 + Math.random() * 120;
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        color,
        radius: 1.5 + Math.random() * 2,
        alpha: 1,
        life: 0,
        maxLife: 0.3 + Math.random() * 0.4,
      });
    }
  }

  private updateParticles(dt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life += dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.alpha = Math.max(0, 1 - p.life / p.maxLife);

      if (p.life >= p.maxLife) {
        this.particles.splice(i, 1);
      }
    }
  }

  private addFloatingText(text: string, x: number, y: number, color: string): void {
    const id = `ft_${this.nextId++}`;
    this.floatingTexts.push({
      id,
      text,
      x,
      y,
      color,
      alpha: 1,
      life: 0,
      maxLife: 0.85,
    });
  }

  private updateFloatingTexts(dt: number): void {
    for (let i = this.floatingTexts.length - 1; i >= 0; i--) {
      const ft = this.floatingTexts[i];
      ft.life += dt;
      ft.y -= 25 * dt;
      ft.alpha = Math.max(0, 1 - ft.life / ft.maxLife);

      if (ft.life >= ft.maxLife) {
        this.floatingTexts.splice(i, 1);
      }
    }
  }

  public getStats(): EngineStats {
    const activeSilosCount = this.silos.filter((s) => s.active).length;
    const activeCitiesCount = this.cities.filter((c) => c.active).length;
    let message: string | undefined = undefined;

    if (this.phase === 'START') {
      message = 'PRESS FIRE / TAP TO START';
    } else if (this.phase === 'GAME_OVER') {
      message = 'GAME OVER - PRESS FIRE / TAP TO RESTART';
    } else if (this.phase === 'WAVE_CLEAR') {
      message = `WAVE ${this.wave} COMPLETED! PREPARING NEXT WAVE...`;
    } else if (this.tripleShotTimer > 0) {
      message = `TRIPLE SALVO ACTIVE (${this.tripleShotTimer.toFixed(1)}s)`;
    }

    return {
      score: this.score,
      wave: this.wave,
      lives: activeSilosCount + activeCitiesCount,
      phase: this.phase,
      combo: this.combo,
      maxCombo: this.maxCombo,
      message,
    };
  }
}
